import type { AppThunk } from "@Store/index";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import getNewsPageData from "./getNewsPageData";
import { fetchRedirects, transformRedirectsToMap } from "./helper";

export type Image = {
  id: number;
  alt: string;
  prefix?: string;
  updatedAt: string;
  createdAt: string;
  url: string;
  thumbnailURL?: string | null;
  filename?: string;
  mimeType?: string;
  filesize?: number;
  width?: number;
  height?: number;
  focalX?: number;
  focalY?: number;
};

export type NewsCategory = {
  id: number;
  name: string;
  slug: string;
  metaDescription?: string;
  title?: string;
  isArchived?: boolean;
  updatedAt: string;
  createdAt: string;
  _status: "published" | "draft";
};

export type NewsTag = {
  id: number;
  name: string;
  slug: string;
  title?: string;
  metaDescription?: string;
  isArchived?: boolean;
  updatedAt: string;
  createdAt: string;
  _status: "published" | "draft";
};

export type PostBodyNode = {
  type: string;
  format?: string;
  indent?: number;
  version?: number;
  children?: any[];
  direction?: string | null;
};

export type PostBody = {
  root: PostBodyNode;
};

export type NewsPost = {
  id: number;
  name: string;
  slug: string;
  postType: "news" | "amber-beat" | "amber-report";
  mainImage: Image;
  altText?: string | null;
  category?: NewsCategory;
  tags?: NewsTag[];
  date?: string;
  readTime?: string;
  postBody?: PostBody;
  tocBasedOn?: string;
  featured?: boolean;
  video?: string | null;
  metaDescription?: string;
  metaKeywords?: string;
  isArchived?: boolean;
  updatedAt: string;
  createdAt: string;
  _status: "published" | "draft";
};

export type NewsPostSimple = {
  id: number;
  name: string;
  slug: string;
  postType: "news" | "amber-beat" | "amber-report";
  mainImage: Image;
  category: NewsCategory;
  date: string;
  updatedAt?: string;
  readTime: string;
};

interface NewsPageData {
  readyStatus: "invalid" | "request" | "success" | "failure";
  slug: string;
  data: NewsPost | null;
  categoryData: any;
  sidebarFeaturedNews: NewsPostSimple[];
  categories: NewsCategory[];
  error: any;
  canonical_url: string | null;
  isCategory: boolean;
  isTag: boolean;
  isSearch: boolean;
  searchQuery: string;
  isLatest: boolean;
  totalDocs: number;
  redirects: Record<string, string>;
}

export const initialState: NewsPageData = {
  readyStatus: "invalid",
  slug: "",
  data: null,
  categoryData: null,
  sidebarFeaturedNews: [],
  categories: [],
  error: null,
  canonical_url: null,
  isCategory: false,
  isTag: false,
  isSearch: false,
  searchQuery: "",
  isLatest: false,
  totalDocs: 0,
  redirects: {},
};

const newsPageSlice = createSlice({
  name: "newsPageData",
  initialState,
  reducers: {
    updateNewsData: (state: NewsPageData, { payload }: PayloadAction<any>) => {
      state.data = payload?.data || null;
      state.categoryData = payload?.categoryData || null;
      state.sidebarFeaturedNews = payload?.sidebarFeaturedNews || [];
      state.categories = payload?.categories || [];
      state.slug = payload.slug;
      state.canonical_url = payload.canonical_url;
      state.isCategory = payload.isCategory || false;
      state.isTag = payload.isTag || false;
      state.isSearch = payload.isSearch || false;
      state.searchQuery = payload.searchQuery || "";
      state.isLatest = payload.isLatest || false;
      state.totalDocs = payload.totalDocs || 0;
    },
    updateNewsReadyStatus: (
      state: NewsPageData,
      { payload }: PayloadAction<"invalid" | "request" | "success" | "failure">,
    ) => {
      state.readyStatus = payload;
    },
    resetNewsData: (state: NewsPageData) => {
      state.readyStatus = "invalid";
      state.data = null;
      state.sidebarFeaturedNews = [];
      state.categories = [];
      state.error = null;
      state.isCategory = false;
      state.isTag = false;
      state.isSearch = false;
      state.searchQuery = "";
      state.isLatest = false;
      state.totalDocs = 0;
    },
    updateNewsRedirects: (state: NewsPageData, { payload }: PayloadAction<any>) => {
      state.redirects = payload;
    },
  },
});

export const { updateNewsData, updateNewsReadyStatus, resetNewsData, updateNewsRedirects } =
  newsPageSlice.actions;

export default newsPageSlice.reducer;

export const shouldFetchNews = (newsPageDataState: NewsPageData, slug: string) =>
  newsPageDataState.readyStatus === "invalid" || newsPageDataState.slug !== slug;

export const fetchNewsPageDataIfNeeded =
  (
    props: any,
    isCategory = false,
    isTag = false,
    isSearch = false,
    searchQuery = "",
    isLatest = false,
  ): AppThunk =>
  async (dispatch, getState: any) => {
    if (props?.staticContext?.statusCode == 301) {
      return;
    }

    let slug: string;
    if (isLatest) {
      slug = "latest";
    } else {
      slug = __CLIENT__ ? props?.match?.params?.slug : props?.params?.slug;
    }
    const href = (__CLIENT__ ? window.location.href : props.req?.url) || null;

    const state = getState();

    let identifier: string;
    if (isLatest) {
      identifier = "latest";
    } else if (isSearch) {
      identifier = searchQuery;
    } else {
      identifier = slug;
    }

    const shouldRefetch = isSearch
      ? state.newsPageData.searchQuery !== searchQuery ||
        shouldFetchNews(state.newsPageData, identifier)
      : shouldFetchNews(state.newsPageData, identifier);

    if (shouldRefetch) {
      await dispatch(
        getNewsPageData({
          slug: identifier,
          url: href,
          staticContext: props.staticContext,
          isCategory,
          isTag,
          isSearch,
          searchQuery,
          isLatest,
        }),
      );
      return;
    }

    if (state.router.action === "PUSH" && __CLIENT__) {
      window.scrollTo(0, 0);
    }

    // eslint-disable-next-line consistent-return
    return null;
  };

export const newsMiddlewareServer =
  (props: any): AppThunk =>
  async (dispatch) => {
    const path = __SERVER__ ? props?.req?.path : window.location.pathname;

    if (!path.startsWith("/news")) {
      return;
    }

    try {
      const response = await fetchRedirects(0);
      const redirectMap = transformRedirectsToMap(response.docs);
      dispatch(updateNewsRedirects(redirectMap));

      const newsPath = path.replace(/^\/news/, "");

      if (redirectMap[newsPath]) {
        const redirectTo = redirectMap[newsPath];

        if (__SERVER__ && props.staticContext) {
          props.staticContext.url = redirectTo;
          props.staticContext.statusCode = 301;
        }
      }
    } catch (error) {
      console.error("[News Redirects] Error fetching redirects:", error);
    }
  };
